import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { isHttpsUrl, isHttpUrl } from '@shared/utils/http-url.utils';
import { catchError, defer, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IImportedData, IFileDataSchema } from '../file-transfer-services/interfaces';
import { SessionStorageService } from '../storage.service';
import { ISymbolCopyPostRequest, ISymbolExport, ISymbolGetResponse, ISymbolSignedUrlResponse, SymbolService } from '../symbol.service';
import { IAccessMap, UserService } from '../user.service';
import { UserPermissionsService } from '../userpermissions.service';
import { FailedImport, FailedParse, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Symbols';
const importConcurrency = 5;

const SYMBOL_CREATE_PERMISSION = 'drawing:SymbolCreate';
/** Needed to export symbols for another environment and to import symbols that come from one. The drawing backend asks for the same permission when it hands out and when it accepts the presigned urls. */
export const SYMBOL_AWS_ENV_IMPORT_EXPORT_PERMISSION = 'drawing:SymbolAwsEnvImportExport';

const SymbolSchema: IFileDataSchema<ISymbolExport> = {
	name: sheetName,
	properties: {
		name: { type: 'string', required: true, fileKey: 'Name', width: 30 },
		description: { type: 'string', required: true, fileKey: 'Description', width: 50 },
		width: { type: 'number', required: true, fileKey: 'Width', width: 12 },
		height: { type: 'number', required: true, fileKey: 'Height', width: 12 },
		basePointX: { type: 'number', required: true, fileKey: 'BasePointX', width: 15 },
		basePointY: { type: 'number', required: true, fileKey: 'BasePointY', width: 15 },
		drawingOrder: { type: 'number', required: true, fileKey: 'DrawingOrder', width: 15 },
		assetKey: { type: 'string', required: true, fileKey: 'AssetKey', width: 70 },
		language: { type: 'string', required: false, fileKey: 'Language', width: 15 },
		isDefault: { type: 'boolean', required: false, fileKey: 'IsDefault', width: 12 },
	},
};

interface SymbolImportContext {
	existingByIdentity: Map<string, ISymbolGetResponse>;
}

/** A row that passed parsing, carrying its file location through to a save failure too. */
interface SymbolImportCandidate {
	symbol: ISymbolExport;
	location?: string;
}

/** The backend answered that the presigned url of a row has expired - kept apart from other create failures so that an update which fails on it can still say why. */
class SymbolLinkExpiredError extends Error {}

@Injectable({
	providedIn: 'root',
})
export class SymbolTransferService implements ITransferService<ISymbolGetResponse> {
	private translationPrefix = 'masterdata.symbols';

	constructor(
		private symbolService: SymbolService,
		private sessionStorageService: SessionStorageService,
		private userPermissionsService: UserPermissionsService,
		private userService: UserService,
		private translator: TranslateService,
		private excelFileTransfer: ExcelFileTransfer<ISymbolExport>,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	public downloadTemplate(): void {
		const exampleRow: ISymbolExport = {
			name: 'Example Symbol',
			description: 'Example description',
			width: 100,
			height: 100,
			basePointX: 0,
			basePointY: 0,
			drawingOrder: 0,
			assetKey: 'accountId/engineId/SVG/symbols/example.svg',
			language: '',
			isDefault: false,
		};

		this.excelFileTransfer.exportData(`${sheetName}Template`, [exampleRow], SymbolSchema).subscribe();
	}

	// A symbol is identified by its name and its language, as in the backend - so, like
	// rack/wagon/rackset, nothing here treats it as unique on faith: both the selection being
	// exported and a re-import of that same file are checked.
	public exportData(symbols: ISymbolGetResponse[], external: boolean = false): Observable<ISymbolGetResponse[]> {
		const duplicates = this.findDuplicateSymbols(symbols);
		if (duplicates.length > 0) {
			return throwError(
				() =>
					new Error(
						this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, {
							names: duplicates.join(', '),
						}),
					),
			);
		}

		const rows$ = external ? this.toExternalExportRows(symbols) : of(symbols.map((symbol) => this.toExportRow(symbol, this.localAssetKey(symbol))));

		// ExcelFileTransfer.exportData writes the file (and validates cell lengths) synchronously
		// before returning its observable, so calling it directly here would let a real write
		// failure throw past catchError below instead of reaching it - defer() delays that call
		// until subscription, inside RxJS's own error handling.
		return rows$.pipe(
			mergeMap((data) => defer(() => this.excelFileTransfer.exportData(sheetName, data, SymbolSchema))),
			map(() => symbols),
			catchError((error) => {
				throw new Error(this.translator.instant(`${this.translationPrefix}.export.errors.failed`, { error: error.message }));
			}),
		);
	}

	// downloadUrl is a plain CDN path with no signature - cross-environment import needs a real,
	// time-limited presigned S3 url instead.
	private toExternalExportRows(symbols: ISymbolGetResponse[]): Observable<ISymbolExport[]> {
		return this.symbolService.getSignedUrls(symbols.map((symbol) => symbol.id)).pipe(
			map((response) => {
				const urlById = new Map<string, string>(response.result.map((signed: ISymbolSignedUrlResponse) => [signed.id, signed.url]));

				return symbols.map((symbol) => {
					const url = urlById.get(symbol.id);
					if (!url) {
						throw new Error(this.translator.instant(`${this.translationPrefix}.export.errors.signed-url-missing`, { name: symbol.name }));
					}
					return this.toExportRow(symbol, url);
				});
			}),
		);
	}

	private localAssetKey(symbol: ISymbolGetResponse): string {
		const account = this.userService.account;
		const file = symbol.downloadUrl.split('?')[0].split('/').pop();
		// {AccountID}/{EngineId}/SVG/{symbolName}.svg
		return `${account.id}/${account.defaultEngine}/SVG/symbols/${file}`;
	}

	private toExportRow(symbol: ISymbolGetResponse, assetKey: string): ISymbolExport {
		return {
			name: symbol.name,
			description: symbol.description,
			width: symbol.width,
			height: symbol.height,
			basePointX: symbol.basePointX,
			basePointY: symbol.basePointY,
			drawingOrder: symbol.drawingOrder,
			assetKey,
			language: symbol.language,
			isDefault: symbol.isDefault,
		};
	}

	private canImportSymbol(assetKey: string): boolean {
		const sourceAccountId = assetKey?.split('/')[0];
		if (!sourceAccountId) {
			return false;
		}

		try {
			const accessMap: IAccessMap | null = JSON.parse(this.sessionStorageService.get('User.AccessMap'));
			if (!accessMap) {
				return false;
			}

			if (accessMap.permissions?.includes(SYMBOL_CREATE_PERMISSION)) {
				return true;
			}

			return (accessMap.organizations ?? []).some((org) => {
				const account = org?.accounts?.find((a) => a?.id === sourceAccountId);
				if (!account) {
					return false;
				}
				return org?.permissions?.includes(SYMBOL_CREATE_PERMISSION) || account?.permissions?.includes(SYMBOL_CREATE_PERMISSION);
			});
		} catch {
			return false;
		}
	}

	public importData(file: File | File[]): Observable<ImportResponse> {
		// Every row ends in creating a symbol, whether it comes from a key in this environment or a link to another: without the permission nothing is read, fetched or deleted.
		if (!this.userPermissionsService.hasPermissions([SYMBOL_CREATE_PERMISSION])) {
			return of({
				successfulImports: [],
				errors: [{ status: ImportStatus.ParsingError, message: this.translator.instant(`${this.translationPrefix}.import.errors.missing-permission-create`) } as FailedParse],
			});
		}

		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed - whatever went wrong is already in ImportErrors. Stop here instead
				// of fetching the roster only to report an unrelated duplicate-identity error that
				// would bury the actual parse failure.
				if (parsedRows.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = this.findDuplicateSymbols(parsedRows.map((imported) => imported.data));

				return this.symbolService.getAll().pipe(
					mergeMap((response) => {
						const rosterDuplicates = this.findDuplicateSymbols(response.result);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const identitiesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						const ctx: SymbolImportContext = {
							existingByIdentity: this.buildSymbolIdentityMap(response.result),
						};

						const candidates: SymbolImportCandidate[] = parsedRows.map((imported) => ({ symbol: imported.data, location: imported.location }));

						return from(identitiesAreAmbiguous ? [] : candidates).pipe(
							mergeMap(this.importSymbol(ctx, ImportErrors), importConcurrency),
							toArray(),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<ISymbolExport>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, SymbolSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				// A space around a cell, a common slip when pasting, is taken off here, before anything asks what the cell is: it would make a url read as a key, and the backend takes a name or a language only as it is spelled, without spaces.
				return from(rows.map((row) => ({ ...row, data: { ...row.data, name: this.trimmed(row.data.name), language: this.trimmed(row.data.language), assetKey: this.trimmed(row.data.assetKey) } })));
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	// Mirrors the cross-environment/same-account permission checks and the delete-then-recreate
	// flow the previous JSON-based importer ran, but isolated per row: a failure here is reported
	// and the row is skipped, instead of aborting every other row in the batch (the previous
	// importer's single subscribe() meant one bad permission or a network blip failed the whole
	// import with one generic message).
	private importSymbol(ctx: SymbolImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: SymbolImportCandidate) => {
			const { symbol, location } = candidate;
			const existing = ctx.existingByIdentity.get(this.identityKey(symbol));

			return defer(() => this.prepareImport(symbol)).pipe(
				mergeMap(() => this.replaceExisting(existing, symbol)),
				mergeMap(() => this.createSymbol(symbol).pipe(catchError((error: Error) => this.recoverFromFailedCreate(existing, symbol, error)))),
				map((entity) => ({
					entity,
					status: existing ? ImportStatus.SuccessUpdate : ImportStatus.SuccessCreate,
				})),
				catchError((error: Error) => {
					this.pushSaveFailedError(ImportErrors, symbol, location, error.message);
					return of();
				}),
			);
		};
	}

	// Restoring only makes sense once replaceExisting() has actually deleted the previous symbol -
	// a prepareImport()/replaceExisting() failure never reaches here, so their own (accurate) error
	// propagates unchanged instead of being overwritten by a misleading "restored"/"missing" message.
	private recoverFromFailedCreate(existing: ISymbolGetResponse | undefined, symbol: ISymbolExport, error: Error): Observable<never> {
		if (!existing) {
			return throwError(() => error);
		}

		// An expired link is the reason the update failed, so the message keeps it instead of only saying the update failed.
		const reason = error instanceof SymbolLinkExpiredError ? '-expired' : '';

		return this.restoreExisting(existing).pipe(mergeMap((restored) => throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.import.errors.${restored ? 'update-failed-restored' : 'update-failed-existing-lost'}${reason}`, { name: symbol.name })))));
	}

	// Best-effort compensation for replaceExisting() having already deleted the previous symbol:
	// deleting only removes the metadata record, not the underlying asset the account still holds
	// (mirrors how exportData() itself derives assetKey from downloadUrl), so a same-account copy
	// from that same asset can put the previous symbol back under a new id. If this also fails, the
	// caller is told plainly that the symbol is now missing rather than merely "not updated".
	private restoreExisting(existing: ISymbolGetResponse): Observable<boolean> {
		const account = this.userService.account;
		const file = existing.downloadUrl.split('?')[0].split('/').pop();
		const request: ISymbolCopyPostRequest = {
			name: existing.name,
			description: existing.description,
			height: existing.height,
			width: existing.width,
			basePointX: existing.basePointX,
			basePointY: existing.basePointY,
			drawingOrder: existing.drawingOrder,
			assetKey: `${account.id}/${account.defaultEngine}/SVG/symbols/${file}`,
			language: existing.language,
			isDefault: existing.isDefault,
		};

		return this.symbolService.createAndCopy(request).pipe(
			map(() => true),
			catchError(() => of(false)),
		);
	}

	// Gates permission, and a presigned url to https - the backend accepts assetKey as a presigned url directly via createAndCopy, over https only.
	private prepareImport(symbol: ISymbolExport): Observable<void> {
		if (isHttpUrl(symbol.assetKey)) {
			// The create permission was asked for at the start of the import; a link from another environment needs this one besides.
			if (!this.userPermissionsService.hasPermissions([SYMBOL_AWS_ENV_IMPORT_EXPORT_PERMISSION])) {
				return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.import.errors.missing-permission-cross-environment`, { name: symbol.name })));
			}
			// The backend takes a presigned url over https only.
			if (!isHttpsUrl(symbol.assetKey)) {
				return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.import.errors.asset-url-not-https`, { name: symbol.name })));
			}
			return of(undefined);
		}

		if (!this.canImportSymbol(symbol.assetKey)) {
			return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.import.errors.missing-permission-source-account`, { name: symbol.name })));
		}

		return of(undefined);
	}

	private replaceExisting(existing: ISymbolGetResponse | undefined, symbol: ISymbolExport): Observable<void> {
		if (!existing) {
			return of(undefined);
		}

		return this.symbolService.delete(existing.name, existing.language).pipe(
			map(() => undefined),
			catchError(() => throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.import.errors.delete-failed`, { name: symbol.name })))),
		);
	}

	// The backend fetches a presigned url server-side before saving anything, so there's no upload step left to roll back on failure.
	private createSymbol(symbol: ISymbolExport): Observable<ISymbolExport> {
		const request: ISymbolCopyPostRequest = {
			name: symbol.name,
			description: symbol.description,
			height: symbol.height,
			width: symbol.width,
			basePointX: symbol.basePointX,
			basePointY: symbol.basePointY,
			drawingOrder: symbol.drawingOrder,
			assetKey: symbol.assetKey,
			language: symbol.language,
			isDefault: symbol.isDefault,
		};

		return this.symbolService.createAndCopy(request).pipe(
			map(() => symbol),
			catchError((error: HttpErrorResponse) => {
				const expired = error.status === 410;
				const message = this.translator.instant(`${this.translationPrefix}.import.errors.${expired ? 'signed-url-expired' : 'create-failed'}`, { name: symbol.name });
				return throwError(() => (expired ? new SymbolLinkExpiredError(message) : new Error(message)));
			}),
		);
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], symbol: ISymbolExport, location: string | undefined, message: string): void {
		ImportErrors.push({
			Id: symbol.name,
			location,
			status: ImportStatus.Failed,
			message,
		} as FailedImport);
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}

	/** A symbol's identity is its name and its language, as in the backend: each ignoring case and surrounding spaces, and no language the same as an empty one. */
	private identityKey(symbol: { name: string; language?: string | null }): string {
		return JSON.stringify([this.normalize(symbol.name), this.normalize(symbol.language)]);
	}

	private buildSymbolIdentityMap(symbols: ISymbolGetResponse[]): Map<string, ISymbolGetResponse> {
		const map = new Map<string, ISymbolGetResponse>();
		symbols.forEach((symbol) => map.set(this.identityKey(symbol), symbol));
		return map;
	}

	/**
	 * Symbols whose name and language appear 2+ times once normalized. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns every row involved in a collision as it is written, with its language when it has one (`Name (da)`), for a message a user
	 * can act on.
	 */
	private findDuplicateSymbols(items: { name: string; language?: string | null }[]): string[] {
		const firstSeen = new Map<string, string>();
		const duplicates = new Set<string>();

		for (const item of items) {
			if (!this.normalize(item.name)) {
				continue;
			}

			const key = this.identityKey(item);
			const label = this.normalize(item.language) ? `${item.name} (${item.language})` : item.name;
			const original = firstSeen.get(key);
			if (original !== undefined) {
				duplicates.add(original);
				duplicates.add(label);
			} else {
				firstSeen.set(key, label);
			}
		}

		return Array.from(duplicates);
	}

	private trimmed<T>(value: T): T {
		return typeof value === 'string' ? (value.trim() as T) : value;
	}

	private normalize(value: unknown): string {
		return value === null || value === undefined ? '' : String(value).trim().toLowerCase();
	}
}
